/**
 * CampaignKeyArt — marketing-asset section on the Statistics view.
 *
 * Lists items grouped by category (poster / banner / still / press /
 * logo / source / other). Each tile shows a preview thumbnail when
 * one was generated, falls back to a filetype label otherwise. Upload
 * affordance accepts single files and zips (server-side unpacks).
 *
 * Server-side preview generation handles JPEG/PNG/PSD/PSB up to 500
 * MiB. PDF, InDesign, and oversized PSBs are stored but show as
 * "preview unavailable — download to view."
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Tooltip,
  Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteIcon,
  InsertDriveFile as FileIcon,
  Share as ShareIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import api from '../services/api';
import CampaignKeyArtShareDialog from './CampaignKeyArtShareDialog';
import CampaignKeyArtDeliveryLog from './CampaignKeyArtDeliveryLog';


const CATEGORY_ORDER = ['poster', 'banner', 'still', 'press', 'logo', 'source', 'other'];
const CATEGORY_LABELS = {
  poster: 'Posters',
  banner: 'Banners',
  still: 'Stills',
  press: 'Press kit',
  logo: 'Logos',
  source: 'Source files',
  other: 'Other',
};


function fmtSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}


// v10.19.42: filetype-specific guidance for files without a web
// preview. Each entry is { label, hint } — short uppercase tag for
// the badge + one-liner telling the recipient/operator how to open
// it. Falls back to a generic "Download to view" for unknown types.
const FILETYPE_HINTS = {
  psb: { label: 'PSB', hint: 'Open in Adobe Photoshop (layered)' },
  psd: { label: 'PSD', hint: 'Open in Adobe Photoshop (layered)' },
  indd: { label: 'INDD', hint: 'Open in Adobe InDesign' },
  idml: { label: 'IDML', hint: 'Open in Adobe InDesign (interchange)' },
  ai:   { label: 'AI',   hint: 'Open in Adobe Illustrator' },
  eps:  { label: 'EPS',  hint: 'Open in Illustrator / Photoshop' },
  pdf:  { label: 'PDF',  hint: 'Print-ready or web — opens in any PDF reader' },
  xd:   { label: 'XD',   hint: 'Open in Adobe XD' },
  sketch: { label: 'SKETCH', hint: 'Open in Sketch (macOS)' },
  fig:  { label: 'FIG',  hint: 'Open in Figma' },
  zip:  { label: 'ZIP',  hint: 'Bundle — unzip to view contents' },
  tar:  { label: 'TAR',  hint: 'Archive — extract with tar / 7-Zip' },
  rar:  { label: 'RAR',  hint: 'Archive — extract with 7-Zip / The Unarchiver' },
  '7z': { label: '7Z',   hint: 'Archive — extract with 7-Zip' },
  doc:  { label: 'DOC',  hint: 'Open in Microsoft Word / Pages' },
  docx: { label: 'DOCX', hint: 'Open in Microsoft Word / Pages' },
  rtf:  { label: 'RTF',  hint: 'Open in any word processor' },
  txt:  { label: 'TXT',  hint: 'Plain text — opens anywhere' },
  ppt:  { label: 'PPT',  hint: 'Open in Microsoft PowerPoint / Keynote' },
  pptx: { label: 'PPTX', hint: 'Open in Microsoft PowerPoint / Keynote' },
  xls:  { label: 'XLS',  hint: 'Open in Microsoft Excel / Numbers' },
  xlsx: { label: 'XLSX', hint: 'Open in Microsoft Excel / Numbers' },
  csv:  { label: 'CSV',  hint: 'Spreadsheet — opens in Excel / Numbers' },
  mov:  { label: 'MOV',  hint: 'Video — opens in QuickTime / VLC' },
  mp4:  { label: 'MP4',  hint: 'Video — opens in any modern player' },
  m4v:  { label: 'M4V',  hint: 'Video — opens in QuickTime / VLC' },
  mkv:  { label: 'MKV',  hint: 'Video — opens in VLC' },
  webm: { label: 'WEBM', hint: 'Video — opens in any modern browser' },
  avi:  { label: 'AVI',  hint: 'Video — opens in VLC' },
  prproj: { label: 'PRPROJ', hint: 'Open in Adobe Premiere Pro' },
  aep:  { label: 'AEP',  hint: 'Open in Adobe After Effects' },
  ttf:  { label: 'TTF',  hint: 'Font — install via Font Book / Windows Fonts' },
  otf:  { label: 'OTF',  hint: 'Font — install via Font Book / Windows Fonts' },
  woff: { label: 'WOFF', hint: 'Web font' },
};

function _hintFor(filename) {
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  return FILETYPE_HINTS[ext] || { label: ext.toUpperCase() || 'FILE', hint: 'Download to view' };
}


// v10.19.31: auth-aware preview fetcher (the endpoint is tenant-scoped
// so a bare <img src> would 401). Loads a blob, attaches as object URL.
// v10.20.1: gains an inline "Upload preview" affordance on the no-
// preview branch so the operator can hand a JPEG for items the auto
// pipeline can't render (PSB over the size cap, INDD, etc).
function PreviewThumb({ campaignId, itemId, hasPreview, alt, filename, onUploadComplete }) {
  const [url, setUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (!hasPreview) { setUrl(null); return; }
    let cancelled = false; let current = null;
    api.get(`/api/campaigns/${campaignId}/key-art/${itemId}/preview`, { responseType: 'blob' })
      .then((r) => {
        if (cancelled) return;
        current = URL.createObjectURL(r.data);
        setUrl(current);
      })
      .catch(() => { /* leave fallback */ });
    return () => { cancelled = true; if (current) URL.revokeObjectURL(current); };
  }, [campaignId, itemId, hasPreview]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/api/campaigns/${campaignId}/key-art/${itemId}/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploadComplete?.();
    } catch (err) {
      // Surface as alert — caller's snackbar wiring isn't piped down here
      // and this is a rare operator-initiated path, not a hot one.
      alert(err?.response?.data?.detail || 'Preview upload failed.');
    } finally {
      setUploading(false);
    }
  };

  if (url) {
    // v10.19.43: object-fit: contain so landscape art doesn't get
    // cropped to fit the (taller-than-its-aspect) 4:3 frame, and
    // portrait art doesn't crop sides on rare odd source aspects.
    return <Box component="img" src={url} alt={alt} sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />;
  }
  if (hasPreview) {
    // Server says there's a preview but we haven't loaded it yet.
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
        <FileIcon fontSize="medium" />
        <Typography variant="caption" sx={{ fontSize: '0.62rem', mt: 0.5 }}>Loading…</Typography>
      </Box>
    );
  }
  // v10.19.42: filetype-aware placeholder. Big badge + how-to-open hint.
  // v10.20.1: + an Upload preview button so a PSB / INDD / oversize file
  // can still get a tile thumbnail from a hand-exported JPEG.
  const h = _hintFor(filename);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 1, py: 1, width: '100%', height: '100%' }}>
      <Box sx={{
        px: 1, py: 0.5, borderRadius: 0.5, border: 1, borderColor: 'divider',
        bgcolor: 'rgba(212,160,23,0.08)', color: 'primary.main',
        fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.5,
        mb: 0.5,
      }}>
        {h.label}
      </Box>
      <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.3, mb: 1 }}>
        {h.hint}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={uploading}
        onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
        sx={{ fontSize: '0.62rem', py: 0.25, px: 0.75, minHeight: 22, lineHeight: 1.2 }}
      >
        {uploading ? 'Uploading…' : 'Upload preview'}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={handleUpload}
        onClick={(e) => e.stopPropagation()}
      />
    </Box>
  );
}


// v10.19.35: image extensions that can be promoted to the campaign
// poster via the "Set as poster" action. Matches the backend allow-
// list in api/campaigns.py:set_key_art_as_poster.
const POSTER_OK_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.tif', '.tiff', '.bmp'];


export default function CampaignKeyArt({ campaignId }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [shareOpen, setShareOpen] = useState(false);
  // v10.19.40: bump after a successful share so the delivery-log
  // section re-pulls without forcing a page reload.
  const [deliveryRefresh, setDeliveryRefresh] = useState(0);
  const fileRef = useRef(null);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const onlySelectedItem = (() => {
    if (selected.size !== 1) return null;
    const id = [...selected][0];
    return (items || []).find((it) => it.id === id) || null;
  })();
  const selectedIsImage = (() => {
    if (!onlySelectedItem) return false;
    const name = (onlySelectedItem.original_filename || '').toLowerCase();
    return POSTER_OK_EXT.some((ext) => name.endsWith(ext));
  })();

  const setAsPoster = async () => {
    if (!onlySelectedItem) return;
    try {
      await api.post(`/api/campaigns/${campaignId}/key-art/${onlySelectedItem.id}/set-as-poster`);
      setMessage({ severity: 'success', text: `Set "${onlySelectedItem.original_filename}" as the campaign poster.` });
    } catch (e) {
      setMessage({ severity: 'error', text: e?.response?.data?.detail || 'Could not set as poster.' });
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/api/campaigns/${campaignId}/key-art`);
      setItems(data || []);
    } catch (_e) {
      setItems([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaignId]);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    uploadFile(f);
  };

  const uploadFile = async (file) => {
    setBusy(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/api/campaigns/${campaignId}/key-art/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.kind === 'zip') {
        setMessage({ severity: 'success', text: `Unpacked ${r.data.registered} file${r.data.registered === 1 ? '' : 's'}${r.data.previews_generated ? ` · ${r.data.previews_generated} preview${r.data.previews_generated === 1 ? '' : 's'} generated` : ''}.` });
      } else {
        setMessage({ severity: 'success', text: `Uploaded ${r.data.original_filename}.` });
      }
      await load();
    } catch (e) {
      setMessage({ severity: 'error', text: e?.response?.data?.detail || 'Upload failed.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.original_filename}" from the key-art set?`)) return;
    try {
      await api.delete(`/api/campaigns/${campaignId}/key-art/${item.id}`);
      await load();
    } catch (_e) { /* */ }
  };

  const download = (item) => {
    // Force the browser to use the original filename + content-disposition
    // by hitting the auth-aware endpoint via a temporary anchor with
    // window.open won't carry the auth header; use a fetch+blob anchor
    // instead.
    api.get(`/api/campaigns/${campaignId}/key-art/${item.id}/download`, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = item.original_filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => {/* */});
  };

  const grouped = (() => {
    const buckets = {};
    (items || []).forEach((it) => {
      const k = it.category || 'other';
      if (!buckets[k]) buckets[k] = [];
      buckets[k].push(it);
    });
    return buckets;
  })();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {selected.size > 0 ? (
          <>
            <Chip
              label={`${selected.size} selected`}
              onDelete={clearSelection}
              size="small"
            />
            <Button
              size="small"
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={() => setShareOpen(true)}
            >
              {selected.size === 1 ? 'Share file' : `Bundle + share (${selected.size})`}
            </Button>
            {selected.size === 1 && selectedIsImage && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<StarIcon />}
                onClick={setAsPoster}
              >
                Set as poster
              </Button>
            )}
          </>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Tick a tile to select it. Single → file. Multiple → zip bundle.
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="contained"
          startIcon={busy ? <CircularProgress size={14} /> : <UploadIcon />}
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          Upload (file, zip, or tar)
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="*/*"
          style={{ display: 'none' }}
          onChange={handlePick}
        />
      </Box>
      <CampaignKeyArtShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        campaignId={campaignId}
        selectedIds={[...selected]}
        selectedItems={(items || []).filter((it) => selected.has(it.id))}
        onSent={() => { clearSelection(); setDeliveryRefresh((n) => n + 1); }}
      />
      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 1.5 }}>
          {message.text}
        </Alert>
      )}

      {items === null && <CircularProgress size={20} />}
      {items !== null && items.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            No key art yet. Upload the producer's press / poster bundle to attach it to this campaign.
          </Typography>
        </Paper>
      )}

      {items !== null && items.length > 0 && CATEGORY_ORDER.map((cat) => {
        const list = grouped[cat] || [];
        if (list.length === 0) return null;
        return (
          <Box key={cat} sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}>
              {CATEGORY_LABELS[cat] || cat}
              <Box component="span" sx={{ color: 'text.disabled' }}> · {list.length}</Box>
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 1.5,
              // v10.19.37: row-stretch was making the landscape tile's
              // outer Paper grow to match the tallest portrait in the
              // same row, so a 16:9 image rendered inside a tall
              // portrait-shaped frame with empty space below. Start-
              // align so each Paper sizes to its own content height.
              alignItems: 'start',
            }}>
              {list.map((it) => {
                // v10.19.44: ALL tile frames lock to the same fixed
                // pixel height so portrait + landscape always align
                // across a row. Aspect-ratio-based sizing in v10.19.43
                // left ~9 px of row-gap drift between 1-col portraits
                // and 2-col landscapes (4:3 × 2-col-with-gap > 2:3 ×
                // 1-col). Image inside uses object-fit: contain so
                // the art slots into the fixed frame without crop.
                const isLandscape = it.width && it.height && it.width > it.height * 1.1;
                const FRAME_HEIGHT = 220;
                return (
                <Paper key={it.id} variant="outlined" sx={{
                  // v10.19.45: fixed outer height so a long filename
                  // wrapping to 2 lines doesn't push a tile taller
                  // than its row-mates. 220 frame + ~110 chrome ≈ 330.
                  p: 1, display: 'flex', flexDirection: 'column',
                  height: 330,
                  gridColumn: isLandscape ? 'span 2' : 'auto',
                  borderColor: selected.has(it.id) ? 'primary.main' : 'divider',
                  borderWidth: selected.has(it.id) ? 2 : 1,
                }}>
                  <Box sx={{
                    height: FRAME_HEIGHT, width: '100%',
                    bgcolor: 'background.default',
                    border: 1, borderColor: 'divider', borderRadius: 0.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', mb: 0.75, position: 'relative',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggle(it.id)}
                  >
                    <PreviewThumb
                      campaignId={campaignId}
                      itemId={it.id}
                      hasPreview={it.has_preview}
                      alt={it.original_filename}
                      filename={it.original_filename}
                      onUploadComplete={load}
                    />
                    <Checkbox
                      size="small"
                      checked={selected.has(it.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(it.id)}
                      sx={{
                        position: 'absolute', top: 2, left: 2,
                        bgcolor: 'rgba(0,0,0,0.45)', borderRadius: '4px',
                        color: '#fff',
                        '&.Mui-checked': { color: '#d4a017' },
                        p: 0.25,
                      }}
                    />
                  </Box>
                  {/* v10.19.39: full filename (wraps if long; break-
                      everywhere so a single long token can still wrap),
                      followed by a meta line carrying extension + size
                      + dimensions, then upload date. */}
                  {/* v10.19.45: clamp filename to 2 lines so a long
                      name doesn't force the tile taller than its row.
                      Title-attr keeps the full name accessible on
                      hover for the rare ≥2-line case. */}
                  <Tooltip title={it.original_filename}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.72rem', fontWeight: 500,
                        wordBreak: 'break-all', lineHeight: 1.25,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {it.original_filename}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: 0.25 }}
                  >
                    {[
                      (it.original_filename.split('.').pop() || '').toUpperCase() || '—',
                      it.width && it.height ? `${it.width}×${it.height}` : null,
                      fmtSize(it.size_bytes),
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                  {it.uploaded_at && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.6rem', color: 'text.disabled' }}
                    >
                      Uploaded {new Date(it.uploaded_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Tooltip title="Download original">
                      <IconButton size="small" onClick={() => download(it)}><DownloadIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleDelete(it)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
                );
              })}
            </Box>
          </Box>
        );
      })}

      {/* v10.19.40: delivery history of past key-art shares — who
          received what, whether they downloaded, when. */}
      <CampaignKeyArtDeliveryLog campaignId={campaignId} refreshKey={deliveryRefresh} />
    </Box>
  );
}
